import { ChevronDown, ChevronUp, DownloadCloud02, Eye, File02, ZoomIn, ZoomOut } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { Link } from 'react-router-dom';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import TabButton from 'src/components/TabButton';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { getInvoiceDetail, trackDocumentDownload } from 'src/services/DocumentsService';
import { getDateWithSuffixFormat, getFormattedAmount, getFormattedDate } from 'src/utils/CommonFunctions';

// Use CDN worker with .mjs extension matching the installed pdfjs-dist v5
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOM_LEVELS = [50, 75, 100, 125, 150];
const PDF_BASE_WIDTH = 850;

interface InvoiceSummaryModalProps {
    invoiceData: any;
    isOpen: boolean;
    handleClose: () => void;
}

const STATUS_BADGE: Record<string, { type: 'success' | 'warning' | 'error' | 'primary' | 'gray'; label: string }> = {
    paid: { type: 'success', label: 'Paid' },
    sent: { type: 'warning', label: 'Pending' },
    partial: { type: 'primary', label: 'Partial' },
};

// Day-of-month suffix (1st, 2nd, 3rd, 4th, …) for the pickup/delivery display strings.
const getOrdinalSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';

    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

const renderDateWithOrdinal = (text: string): React.ReactNode => {
    const parts = text.split(/(\b[A-Z][a-z]{2} \d{1,2},)/);

    return parts.map((part, i) => {
        const match = part.match(/^([A-Z][a-z]{2} )(\d{1,2})(,)$/);

        if (match) {
            const day = parseInt(match[2], 10);

            return (
                <React.Fragment key={i}>
                    {match[1]}
                    {day}
                    <sup className="text-[8px]">{getOrdinalSuffix(day)}</sup>
                    {match[3]}
                </React.Fragment>
            );
        }

        return part;
    });
};

const InvoiceSummaryModal: React.FC<InvoiceSummaryModalProps> = ({ invoiceData, isOpen, handleClose }) => {
    const { t } = useTranslation();

    const [currentStep, setCurrentStep] = useState(1);
    const [detail, setDetail] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPaymentsExpanded, setIsPaymentsExpanded] = useState(false);
    const [isSubtotalExpanded, setIsSubtotalExpanded] = useState(false);

    // PDF viewer state
    const [numPages, setNumPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(50);
    const [pdfNaturalHeight, setPdfNaturalHeight] = useState(0);

    const addressRef = useRef<HTMLElement | null>(null);
    const orderFromOrgNameRef = useRef<HTMLParagraphElement>(null);
    const orderFromAddressRef = useRef<HTMLParagraphElement>(null);
    const pickupDisplayRef = useRef<HTMLParagraphElement>(null);
    const orderToOrgNameRef = useRef<HTMLParagraphElement>(null);
    const orderToAddressRef = useRef<HTMLParagraphElement>(null);
    const deliveryDisplayRef = useRef<HTMLParagraphElement>(null);
    const pdfInnerRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const lastZoomAtRef = useRef(0);
    const zoomAnchorRef = useRef<{ oldZoom: number; cursorX: number; cursorY: number; contentX: number; contentY: number } | null>(null);

    useEffect(() => {
        if (!isOpen || !invoiceData?.id) return;
        setIsLoading(true);
        setDetail(null);
        getInvoiceDetail(invoiceData.id)
            .then((res: any) => setDetail(res?.data ?? null))
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, [isOpen, invoiceData?.id]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setNumPages(0);
            setZoomLevel(50);
            setPdfNaturalHeight(0);
        }
    }, [isOpen]);

    // Track natural height of rendered PDF for zoom scaling
    useEffect(() => {
        const node = pdfInnerRef.current;
        if (!node) return;
        const ro = new ResizeObserver((entries) => {
            const h = entries[0]?.contentRect?.height ?? 0;
            if (h > 0) setPdfNaturalHeight(h);
        });
        ro.observe(node);

        return () => ro.disconnect();
    }, [numPages, detail]);

    const stepZoom = useCallback((direction: 1 | -1, cursor?: { clientX: number; clientY: number }) => {
        setZoomLevel((current) => {
            const idx = ZOOM_LEVELS.indexOf(current);
            const nextIdx = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, (idx === -1 ? 2 : idx) + direction));
            const next = ZOOM_LEVELS[nextIdx];
            const scrollEl = scrollContainerRef.current;

            if (cursor && scrollEl && next !== current) {
                const rect = scrollEl.getBoundingClientRect();
                zoomAnchorRef.current = {
                    oldZoom: current,
                    cursorX: cursor.clientX - rect.left,
                    cursorY: cursor.clientY - rect.top,
                    contentX: scrollEl.scrollLeft + cursor.clientX - rect.left,
                    contentY: scrollEl.scrollTop + cursor.clientY - rect.top,
                };
            }

            return next;
        });
    }, []);

    // Ctrl+wheel zoom — must be after stepZoom definition
    useEffect(() => {
        const node = previewContainerRef.current;
        if (!node) return;

        const onWheel = (e: WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            const now = Date.now();
            if (now - lastZoomAtRef.current < 80) return;
            lastZoomAtRef.current = now;
            stepZoom(e.deltaY < 0 ? 1 : -1, { clientX: e.clientX, clientY: e.clientY });
        };
        node.addEventListener('wheel', onWheel, { passive: false });

        return () => node.removeEventListener('wheel', onWheel);
    }, [stepZoom]);

    useLayoutEffect(() => {
        const scrollEl = scrollContainerRef.current;
        const anchor = zoomAnchorRef.current;
        if (!scrollEl || !anchor) return;
        const ratio = zoomLevel / anchor.oldZoom;
        scrollEl.scrollLeft = anchor.contentX * ratio - anchor.cursorX;
        scrollEl.scrollTop = anchor.contentY * ratio - anchor.cursorY;
        zoomAnchorRef.current = null;
    }, [zoomLevel]);

    // Must be before early return — hooks cannot be called conditionally
    const pdfUrl: string | null = useMemo(() => detail?.invoiceDocuments?.[0]?.downloadUrl ?? detail?.invoicePdf?.downloadUrl ?? null, [detail]);

    if (!isOpen) return null;

    const statusId = detail?.statusId ?? invoiceData?.invoiceStatus?.toLowerCase() ?? '';
    const statusBadge = STATUS_BADGE[statusId] ?? { type: 'gray' as const, label: statusId || 'Created' };
    const currency = detail?.currency ?? invoiceData?.currency ?? 'CAD';
    const order = detail?.order;

    // Same thousands-separated format as the table's Total Price cell
    const formatAmount = (val: string | number | null | undefined) => (val != null ? `$${getFormattedAmount(val)} ${currency}` : '-');

    const handleDownload = async () => {
        if (!pdfUrl) return;
        const name = `${detail?.invoiceNumber || invoiceData?.invoiceNumber || 'invoice'}.pdf`;

        // Portal usage: report the download (fire-and-forget) before the fetch, so a blocked popup still counts.
        trackDocumentDownload({ docType: 'invoice', entityType: 'invoice', entityId: invoiceData?.id ?? detail?.id });

        try {
            const response = await fetch(pdfUrl);
            const blob = await response.blob();
            // Force PDF MIME type so browsers save as .pdf instead of .txt
            const pdfBlob = new Blob([blob], { type: 'application/pdf' });
            const objectUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch {
            window.open(pdfUrl, '_blank');
        }
    };

    const lineItems: any[] = (detail?.orderInvoiceItems || []).map((item: any) => ({
        name:
            item.billingItemDescription ||
            item.billingItemType
                ?.split('_')
                .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' '),
        amount: item.billingItemAmount || 0,
    }));

    const payments: any[] = detail?.payments || [];
    const fuelAmount: number = parseFloat(detail?.fuelAmount ?? '0');

    // Preview is the first AND default tab (client request 2026-09-15) — the row link reads
    // "View Invoice", so the document itself must be what opens.
    const tabArray = [
        { value: 1, name: t('DocumentHub.Preview'), icon: <Eye className="vertical-secondary-tab-item-icon flex-none" /> },
        { value: 2, name: t('DocumentHub.Invoice_Details'), icon: <File02 className="vertical-secondary-tab-item-icon flex-none" /> },
    ];

    const pickupDisplay = order?.scheduledPickupDisplay && order?.scheduledPickupDisplay !== 'N/A' ? order.scheduledPickupDisplay : '-';
    const deliveryDisplay = order?.scheduledDeliveryDisplay && order?.scheduledDeliveryDisplay !== 'N/A' ? order.scheduledDeliveryDisplay : '-';

    return (
        <CommonModal
            title={
                <div className="flex items-center gap-1.5">
                    <span>{detail?.invoiceNumber ? `#${detail.invoiceNumber}` : invoiceData?.invoiceNumber ? `#${invoiceData.invoiceNumber}` : t('DocumentHub.Invoice_Summary')}</span>
                    <BadgeCmp style="modern" type={statusBadge.type}>
                        {statusBadge.label}
                    </BadgeCmp>
                </div>
            }
            handleClose={handleClose}
            size="max-w-[980px] h-[865px]"
            modalContentSidebarClass="!overflow-hidden !scrollbar-hide"
            modalClassName="overflow-hidden min-w-0 flex-1 h-full flex flex-col"
            ModalWithSidebar={true}
            ContentLeft={
                <TabButton
                    activeClassName="tab-box-vertical-active"
                    isVerticalBoxTab={true}
                    tabParentClassName="min-h-[600px]"
                    isModalTabVerticalSidebar={true}
                    tabArray={tabArray}
                    isActive={currentStep}
                    handleOnClick={(e: any) => setCurrentStep(Number(e.target.dataset.value))}
                />
            }
        >
            <div className="flex flex-row w-full flex-1 h-full overflow-hidden">
                {/* ── Tab 2: Invoice Details ── */}
                {currentStep === 2 && (
                    <div className="flex flex-col w-full h-full overflow-hidden">
                        <div className="flex flex-col gap-4 overflow-y-auto custom-scrollbar-v2 px-4 pt-4 pb-4 flex-1">
                            {/* Top info grid */}
                            <div className="bg-gray25 rounded-xl border border-utilityGray200 flex shadow-xs">
                                <div className="flex-1 px-4 py-3 flex flex-col justify-center gap-1 min-h-[64px]">
                                    <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('DocumentHub.Invoice_ID')}</p>
                                    {isLoading ? (
                                        <div className="custom-loading h-[18px] w-20 rounded" />
                                    ) : (
                                        <p className="text-xs leading-[18px] font-normal text-grayLight600">{detail?.invoiceNumber ? `#${detail.invoiceNumber}` : '-'}</p>
                                    )}
                                </div>
                                <div className="flex-1 px-4 py-3 flex flex-col justify-center gap-1 min-h-[64px]">
                                    <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('DocumentHub.Due_Date')}</p>
                                    {isLoading ? (
                                        <div className="custom-loading h-[18px] w-28 rounded" />
                                    ) : (
                                        <p
                                            className="text-xs leading-[18px] font-normal text-grayLight600"
                                            dangerouslySetInnerHTML={{ __html: detail?.dueDate ? getDateWithSuffixFormat(getFormattedDate(detail.dueDate, 'Do MMM, YYYY')) : '-' }}
                                        />
                                    )}
                                </div>
                                <div className="flex-1 px-4 py-3 flex flex-col justify-center gap-1 min-h-[64px]">
                                    <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('DocumentHub.Invoice_Sent')}</p>
                                    {isLoading ? (
                                        <div className="custom-loading h-[18px] w-28 rounded" />
                                    ) : (
                                        <p
                                            className="text-xs leading-[18px] font-normal text-grayLight600"
                                            dangerouslySetInnerHTML={{ __html: detail?.sentAt ? getDateWithSuffixFormat(getFormattedDate(detail.sentAt, 'Do MMM, YYYY')) : '-' }}
                                        />
                                    )}
                                </div>
                                <div className="flex-1 px-4 py-3 flex flex-col justify-center gap-1 min-h-[64px]">
                                    <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('DocumentHub.Payment_Terms')}</p>
                                    {isLoading ? (
                                        <div className="custom-loading h-[18px] w-14 rounded-md" />
                                    ) : detail?.paymentTerms ? (
                                        <BadgeCmp style="modern" type="success" mainClassName="w-fit">
                                            {detail.paymentTerms}
                                        </BadgeCmp>
                                    ) : (
                                        <p className="text-xs leading-[18px] font-normal text-grayLight600">-</p>
                                    )}
                                </div>
                            </div>
                            {/* Customer Details */}
                            <div className="flex flex-col gap-1.5">
                                <h3 className="text-xs leading-[18px] font-medium text-grayLight900">{t('DocumentHub.Customer_Details')}</h3>
                                <div className="rounded-[12px] bg-gray25 border border-utilityGray200 flex flex-col overflow-hidden shadow-xs">
                                    {/* Entity header row */}
                                    <div className="flex items-center gap-2 px-3 py-2 bg-gray25 border-b border-utilityGray200">
                                        {isLoading ? (
                                            <div className="w-[35px] h-[35px] rounded-md flex-none custom-loading" />
                                        ) : detail?.customer?.imageUrl && detail?.customer?.image ? (
                                            <div className="w-[35px] h-[35px] rounded-md border border-black/[0.08] overflow-hidden flex-none bg-white">
                                                <img src={`${detail.customer.imageUrl}${detail.customer.image}`} alt={detail.customer.name} className="w-full h-full object-contain" />
                                            </div>
                                        ) : (
                                            <label className="w-[35px] h-[35px] rounded-md border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center uppercase bg-primary text-white text-sm">
                                                {/* trim() before charAt: customer names can arrive with leading whitespace
                                                    (e.g. " Life in Green Products Inc"), which made the initial a blank space */}
                                                <div>{((detail?.customer?.name || detail?.billingAccount?.roseRocketCustomerName || '').trim() || '?').charAt(0).toUpperCase()}</div>
                                            </label>
                                        )}
                                        <div className="flex-1 min-w-0 flex flex-col gap-1">
                                            {isLoading ? (
                                                <>
                                                    <div className="custom-loading h-[18px] w-32 rounded" />
                                                    <div className="custom-loading h-[18px] w-40 rounded" />
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-xs leading-[18px] font-medium text-grayLight900 truncate">
                                                        {detail?.customer?.name || detail?.billingAccount?.roseRocketCustomerName || '-'}
                                                    </p>
                                                    <p className="text-xs leading-[18px] font-normal text-grayLight600 truncate">{detail?.billingAccount?.displayName || ''}</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Legal Name / Address */}
                                    <div className="flex flex-col px-3 py-3 bg-gray25 gap-1">
                                        <div className="flex items-center gap-1 flex-none">
                                            <span className="text-xs leading-[18px] text-gray500 font-normal flex-none text-nowrap min-w-[80px]">{t('DocumentHub.Legal_Name')}:</span>
                                            {isLoading ? (
                                                <div className="custom-loading h-[18px] w-36 rounded" />
                                            ) : (
                                                <span className="text-xs leading-[18px] font-normal text-grayLight900">{detail?.customer?.name || '-'}</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 flex-1 min-w-0">
                                            <span className="text-xs leading-[18px] text-gray500 font-normal flex-none text-nowrap min-w-[80px]">{t('Common.Address')}:</span>
                                            {isLoading ? (
                                                <div className="custom-loading h-[18px] w-48 rounded" />
                                            ) : (
                                                <TruncateTextCmp
                                                    refName={addressRef}
                                                    truncateTextParentClassName="flex"
                                                    parentClassName="flex"
                                                    message={[detail?.customer?.address1, detail?.customer?.city, detail?.customer?.state, detail?.customer?.postal].filter(Boolean).join(', ') || '-'}
                                                >
                                                    <span ref={addressRef} className="text-xs leading-[18px] font-normal text-grayLight900 truncate">
                                                        {[detail?.customer?.address1, detail?.customer?.city, detail?.customer?.state, detail?.customer?.postal].filter(Boolean).join(', ') || '-'}
                                                    </span>
                                                </TruncateTextCmp>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Address Details */}
                            {(order?.orderFromOrgName || order?.orderToOrgName || isLoading) && (
                                <div className="flex flex-col gap-1.5">
                                    <h3 className="text-xs leading-[18px] font-medium text-grayLight900">{t('DocumentHub.Address_Details')}</h3>
                                    <div className="flex gap-0 p-3 bg-gray25 rounded-[12px] border border-utilityGray200 shadow-xs min-w-0">
                                        {/* Shipper */}
                                        <div className="flex-1 basis-0 min-w-0 flex flex-col gap-5">
                                            {isLoading ? (
                                                <div className="flex flex-col gap-5">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="custom-loading h-[18px] w-36 rounded" />
                                                        <div className="custom-loading h-[18px] w-44 rounded" />
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <div className="custom-loading h-[18px] w-12 rounded" />
                                                        <div className="custom-loading h-[18px] w-28 rounded" />
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-1">
                                                        <TruncateTextCmp message={order?.orderFromOrgName || '-'} refName={orderFromOrgNameRef}>
                                                            <p className="text-xs leading-[18px] font-medium text-grayLight900 truncate" ref={orderFromOrgNameRef}>
                                                                {order?.orderFromOrgName || '-'}
                                                            </p>
                                                        </TruncateTextCmp>
                                                        <TruncateTextCmp message={order?.orderFromAddress || '-'} refName={orderFromAddressRef}>
                                                            <p className="text-xs leading-[18px] font-normal text-grayLight600 truncate" ref={orderFromAddressRef}>
                                                                {order?.orderFromAddress || '-'}
                                                            </p>
                                                        </TruncateTextCmp>
                                                    </div>
                                                    <div className="text-xs leading-[18px] font-normal flex gap-0.5 min-w-0">
                                                        <p className="text-grayLight600 font-normal">Pickup: </p>
                                                        <TruncateTextCmp message={pickupDisplay} refName={pickupDisplayRef}>
                                                            <p className="text-grayLight900 truncate" ref={pickupDisplayRef}>
                                                                {renderDateWithOrdinal(pickupDisplay)}
                                                            </p>
                                                        </TruncateTextCmp>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="w-px self-stretch bg-utilityGray200 mx-3 min-w-[1px] flex-none" />
                                        {/* Consignee */}
                                        <div className="flex-1 basis-0 min-w-0 flex flex-col gap-5">
                                            {isLoading ? (
                                                <div className="flex flex-col gap-5">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="custom-loading h-[18px] w-36 rounded" />
                                                        <div className="custom-loading h-[18px] w-44 rounded" />
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <div className="custom-loading h-[18px] w-14 rounded" />
                                                        <div className="custom-loading h-[18px] w-28 rounded" />
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-1">
                                                        <TruncateTextCmp message={order?.orderToOrgName || '-'} refName={orderToOrgNameRef}>
                                                            <p className="text-xs leading-[18px] font-medium text-grayLight900 truncate" ref={orderToOrgNameRef}>
                                                                {order?.orderToOrgName || '-'}
                                                            </p>
                                                        </TruncateTextCmp>
                                                        <TruncateTextCmp message={order?.orderToAddress || '-'} refName={orderToAddressRef}>
                                                            <p className="text-xs leading-[18px] font-normal text-grayLight600 truncate" ref={orderToAddressRef}>
                                                                {order?.orderToAddress || '-'}
                                                            </p>
                                                        </TruncateTextCmp>
                                                    </div>
                                                    <div className="text-xs leading-[18px] font-normal flex gap-0.5 min-w-0">
                                                        <p className="text-grayLight600">Delivery: </p>
                                                        <TruncateTextCmp message={deliveryDisplay} refName={deliveryDisplayRef}>
                                                            <p className="text-grayLight900 truncate" ref={deliveryDisplayRef}>
                                                                {renderDateWithOrdinal(deliveryDisplay)}
                                                            </p>
                                                        </TruncateTextCmp>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Order Details */}
                            <div className="flex flex-col gap-1.5">
                                <h3 className="text-xs leading-[18px] font-medium text-grayLight900">{t('Orders.Order_Details')}</h3>
                                <div className="bg-gray25 rounded-[12px] border border-utilityGray200 shadow-xs flex">
                                    <div className="flex-1 px-4 py-3 flex flex-col gap-0.5">
                                        <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('Common.Order_ID')}</p>
                                        {isLoading ? (
                                            <div className="custom-loading h-[18px] w-20 rounded" />
                                        ) : detail?.walOrderId && detail?.orderId ? (
                                            <Link to={`/orders/${detail.walOrderId}`} className="text-xs leading-[18px] font-normal text-grayLight600 hover:text-primary">
                                                {`#${detail.orderId}`}
                                            </Link>
                                        ) : (
                                            <p className="text-xs leading-[18px] font-normal text-grayLight600">{detail?.orderId ? `#${detail.orderId}` : '-'}</p>
                                        )}
                                    </div>
                                    <div className="flex-1 px-3 py-2.5 flex flex-col gap-0.5">
                                        <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('Common.Total_Weight')}</p>
                                        {isLoading ? (
                                            <div className="custom-loading h-[18px] w-16 rounded" />
                                        ) : (
                                            <p className="text-xs leading-[18px] font-normal text-grayLight600">{order?.totalWeight ? `${order.totalWeight} lbs` : '-'}</p>
                                        )}
                                    </div>
                                    <div className="flex-1 px-3 py-2.5 flex flex-col gap-0.5">
                                        <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('Common.Linear_Footage')}</p>
                                        {isLoading ? (
                                            <div className="custom-loading h-[18px] w-16 rounded" />
                                        ) : (
                                            <p className="text-xs leading-[18px] font-normal text-grayLight600">{order?.linearFootage ? `${order.linearFootage} Ft.` : '-'}</p>
                                        )}
                                    </div>
                                    <div className="flex-1 px-3 py-2.5 flex flex-col gap-0.5">
                                        <p className="text-xs leading-[18px] font-semibold text-textSecondary">{t('Common.Equipment_Type')}</p>
                                        {isLoading ? (
                                            <div className="custom-loading h-[18px] w-20 rounded-md" />
                                        ) : order?.equipmentTypeFullName || order?.equipmentType ? (
                                            <BadgeCmp style="modern" type="success" mainClassName="w-fit">
                                                {order.equipmentTypeFullName || order.equipmentType}
                                            </BadgeCmp>
                                        ) : (
                                            <p className="text-xs leading-[18px] font-normal text-grayLight600">-</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Invoice Summary */}
                            <div className="flex flex-col gap-1.5">
                                <h3 className="text-xs leading-[18px] font-medium text-grayLight900">{t('DocumentHub.Invoice_Summary')}</h3>
                                <div className="p-3 bg-gray25 shadow-xs rounded-[12px] border border-utilityGray200 flex flex-col">
                                    {isLoading ? (
                                        <div className="flex flex-col">
                                            <div className="flex justify-between items-center">
                                                <div className="custom-loading h-[18px] w-16 rounded" />
                                                <div className="custom-loading h-[18px] w-24 rounded" />
                                            </div>
                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />
                                            <div className="flex justify-between items-center">
                                                <div className="custom-loading h-[18px] w-24 rounded" />
                                                <div className="custom-loading h-[18px] w-24 rounded" />
                                            </div>
                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />
                                            <div className="flex justify-between items-center">
                                                <div className="custom-loading h-[18px] w-20 rounded" />
                                                <div className="custom-loading h-[18px] w-24 rounded" />
                                            </div>
                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />
                                            <div className="flex justify-between items-center">
                                                <div className="custom-loading h-[18px] w-24 rounded" />
                                                <div className="custom-loading h-[18px] w-28 rounded" />
                                            </div>
                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />
                                            <div className="flex justify-between items-center">
                                                <div className="custom-loading h-[18px] w-28 rounded" />
                                                <div className="custom-loading h-[18px] w-24 rounded" />
                                            </div>
                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />
                                            <div className="flex justify-between items-center">
                                                <div className="custom-loading h-[18px] w-32 rounded" />
                                                <div className="custom-loading h-[18px] w-28 rounded" />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Sub-total (expandable) */}
                                            <div className="flex justify-between items-center">
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-1"
                                                    onClick={() => lineItems.length > 0 && setIsSubtotalExpanded((p) => !p)}
                                                    disabled={lineItems.length === 0}
                                                >
                                                    <span className="text-xs leading-[18px] font-medium text-textSecondary">{t('DocumentHub.Sub_Total')}</span>
                                                    {lineItems.length > 0 &&
                                                        (isSubtotalExpanded ? <ChevronUp className="w-3.5 h-3.5 text-textSecondary" /> : <ChevronDown className="w-3.5 h-3.5 text-textSecondary" />)}
                                                </button>
                                                <span className="text-xs leading-[18px] font-medium text-grayLight600">{formatAmount(detail?.subtotalAmount)}</span>
                                            </div>

                                            {/* Expandable line items */}
                                            <div
                                                className={`flex flex-col gap-1.5 overflow-hidden transition-all duration-300 ease-in-out ${
                                                    isSubtotalExpanded ? 'max-h-96 opacity-100 pt-1.5' : 'max-h-0 opacity-0'
                                                }`}
                                            >
                                                {lineItems.map((item: any, idx: number) => (
                                                    <div key={idx} className="flex justify-between items-start">
                                                        <span className="text-[11px] leading-4 font-normal text-gray500">{item.name}</span>
                                                        <span className="text-[11px] leading-4 font-normal text-gray500">{formatAmount(item.amount)}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />

                                            {/* Fuel Surcharge */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs leading-[18px] font-medium text-textSecondary">{t('DocumentHub.Fuel_Surcharge')}</span>
                                                <span className="text-xs leading-[18px] font-medium text-grayLight600">{formatAmount(fuelAmount)}</span>
                                            </div>

                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />

                                            {/* Taxes */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs leading-[18px] font-medium text-textSecondary">
                                                    {t('DocumentHub.Taxes')} ({detail?.taxRate ?? 0}%)
                                                </span>
                                                <span className="text-xs leading-[18px] font-medium text-grayLight600">{formatAmount(detail?.taxAmount)}</span>
                                            </div>

                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />

                                            {/* Total */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs leading-[18px] font-medium text-textSecondary">{t('DocumentHub.Total_Amount')}</span>
                                                <span className="text-xs leading-[18px] font-medium text-grayLight600">{formatAmount(detail?.totalAmount)}</span>
                                            </div>

                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />

                                            {/* Payments Received (expandable) */}
                                            <div className="flex justify-between items-center">
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-1"
                                                    onClick={() => payments.length > 0 && setIsPaymentsExpanded((p) => !p)}
                                                    disabled={payments.length === 0}
                                                >
                                                    <span className="text-xs leading-[18px] font-medium text-textSecondary">{t('DocumentHub.Payments_Received')}</span>
                                                    {payments.length > 0 &&
                                                        (isPaymentsExpanded ? <ChevronUp className="w-3.5 h-3.5 text-textSecondary" /> : <ChevronDown className="w-3.5 h-3.5 text-textSecondary" />)}
                                                </button>
                                                <span className="text-xs leading-[18px] font-medium text-grayLight600">{formatAmount(detail?.invoiceAmountPaid ?? '0')}</span>
                                            </div>

                                            {/* Expandable payment items */}
                                            <div
                                                className={`flex flex-col gap-1.5 overflow-hidden transition-all duration-300 ease-in-out ${
                                                    isPaymentsExpanded ? 'max-h-96 opacity-100 pt-1.5' : 'max-h-0 opacity-0'
                                                }`}
                                            >
                                                {payments.map((p: any, i: number) => (
                                                    <div key={i} className="flex justify-between items-start">
                                                        <span
                                                            className="text-[11px] leading-4 font-normal text-gray500"
                                                            dangerouslySetInnerHTML={{ __html: p.paymentDate ? getDateWithSuffixFormat(getFormattedDate(p.paymentDate, 'MMM Do, YYYY')) : '-' }}
                                                        />
                                                        <span className="text-[11px] leading-4 font-normal text-gray500">
                                                            {p.amount != null ? `$${parseFloat(p.amount).toFixed(2)} ${(p.currency ?? currency).toUpperCase()}` : '-'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="border-dashed border-t border-utilityGray200 my-1.5" />

                                            {/* Outstanding Balance */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-xs leading-[18px] font-semibold text-textSecondary">{t('DocumentHub.Outstanding_Balance')}</span>
                                                <span className="text-xs leading-[18px] font-semibold text-grayLight600">{formatAmount(detail?.outstandingAmount)}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Tab 1: Preview ── */}
                {currentStep === 1 && (
                    <div className="flex flex-col w-full h-full overflow-hidden">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between px-4 pt-4 bg-white800 flex-none space-x-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-semibold text-textSecondary truncate block">Preview</span>
                            </div>
                            <div className="flex items-center gap-2 flex-none">
                                {pdfUrl && !isLoading && (
                                    <>
                                        <TooltipCmp message={t('Common.Zoom_In')}>
                                            <ButtonCmp
                                                size="none"
                                                icon={<ZoomIn className="w-4 h-4 text-grayLight600" />}
                                                onClick={() => stepZoom(1)}
                                                disabled={zoomLevel >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                                            >
                                                <></>
                                            </ButtonCmp>
                                        </TooltipCmp>
                                        <TooltipCmp message={t('Common.Zoom_Out')}>
                                            <ButtonCmp size="none" icon={<ZoomOut className="w-4 h-4 text-grayLight600" />} onClick={() => stepZoom(-1)} disabled={zoomLevel <= ZOOM_LEVELS[0]}>
                                                <></>
                                            </ButtonCmp>
                                        </TooltipCmp>
                                        <div className="w-px h-4 bg-utilityGray200" />
                                    </>
                                )}
                                <TooltipCmp message={t('Common.Download')}>
                                    <ButtonCmp size="none" icon={<DownloadCloud02 className="w-4 h-4 text-grayLight600" />} onClick={handleDownload} disabled={!pdfUrl}>
                                        <></>
                                    </ButtonCmp>
                                </TooltipCmp>
                            </div>
                        </div>

                        {/* PDF Canvas Area */}
                        <div ref={previewContainerRef} className="flex-1 overflow-auto custom-scrollbar-v2 p-5 bg-white800">
                            {isLoading ? (
                                <div className="h-full custom-loading rounded-xl" />
                            ) : pdfUrl ? (
                                <div ref={scrollContainerRef} className="w-full h-full overflow-auto custom-scrollbar-v2 rounded-xl bg-white800 p-8">
                                    <div
                                        className="mx-auto"
                                        style={{
                                            width: PDF_BASE_WIDTH * (zoomLevel / 100),
                                            height: pdfNaturalHeight ? pdfNaturalHeight * (zoomLevel / 100) : undefined,
                                        }}
                                    >
                                        <div
                                            ref={pdfInnerRef}
                                            style={{
                                                width: PDF_BASE_WIDTH,
                                                transform: `scale(${zoomLevel / 100})`,
                                                transformOrigin: 'top left',
                                            }}
                                        >
                                            <Document
                                                file={pdfUrl}
                                                onLoadSuccess={({ numPages: total }) => setNumPages(total)}
                                                loading={<p className="text-sm text-grayLight600 text-center py-16">{t('Common.Loading')}</p>}
                                                error={<p className="text-sm text-grayLight600 text-center py-16">{t('DocumentHub.No_Preview_Available')}</p>}
                                            >
                                                {Array.from({ length: numPages }, (_, i) => (
                                                    <Page
                                                        key={`page_${i + 1}`}
                                                        pageNumber={i + 1}
                                                        width={PDF_BASE_WIDTH}
                                                        renderAnnotationLayer={false}
                                                        renderTextLayer={false}
                                                        className="mb-4 shadow-md bg-white"
                                                    />
                                                ))}
                                            </Document>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center text-sm text-grayLight600">{t('DocumentHub.No_Preview_Available')}</div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-4 pb-4 flex-none bg-white800">
                            {detail?.sentAt && (
                                <p
                                    className="text-xs leading-[18px] text-gray500 font-normal"
                                    dangerouslySetInnerHTML={{
                                        __html: getDateWithSuffixFormat(getFormattedDate(detail.sentAt, 'MMMM Do, YYYY', true, true)),
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </CommonModal>
    );
};

export default InvoiceSummaryModal;
